import { useId, useState, type KeyboardEvent } from 'react';
import {
  PROVIDER_IDS,
  ROUTING_POLICY_NAMES,
  type ProviderId,
  type RoutingPolicyName,
  type RoutingRule,
} from '@trivio/contracts';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { Card } from '../../components/ui/Card';
import { Label } from '../../components/ui/Label';
import { Input } from '../../components/ui/Input';
import { FieldError } from '../../components/ui/FieldError';
import { providerIdPt } from '../../utils/enums-ptbr';
import { routingPolicyNamePt, ruleSummary } from './policySchema';

const POSTAL_PREFIX_PATTERN = /^\d{1,8}$/;

interface PostalPrefixChipsProps {
  value: string[];
  onChange: (next: string[]) => void;
  id: string;
}

/** CEP prefix chip input — type digits, `Enter`/`,` commits a chip, `Backspace` on an empty input removes the last one. */
function PostalPrefixChips({ value, onChange, id }: PostalPrefixChipsProps) {
  const [input, setInput] = useState('');

  function commit() {
    const trimmed = input.trim();
    if (POSTAL_PREFIX_PATTERN.test(trimmed) && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Backspace' && input === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-none border border-input bg-background px-2 py-2 focus-within:ring-2 focus-within:ring-ring/50">
      {value.map((prefix) => (
        <span
          key={prefix}
          className="inline-flex items-center gap-1 rounded-pill border border-border px-2 py-1 font-mono text-xs text-foreground"
        >
          {prefix}
          <button
            type="button"
            onClick={() => onChange(value.filter((p) => p !== prefix))}
            aria-label={`Remover prefixo ${prefix}`}
            className="text-muted-foreground hover:text-accent"
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={id}
        value={input}
        onChange={(event) => setInput(event.target.value.replace(/\D/g, '').slice(0, 8))}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder="Ex.: 01"
        className="min-w-[4rem] flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
      />
    </div>
  );
}

export interface RuleFieldErrors {
  match?: string;
  action?: string;
  weightMin?: string;
}

interface RuleEditorProps {
  rule: RoutingRule;
  index: number;
  total: number;
  fieldErrors?: RuleFieldErrors;
  onChange: (rule: RoutingRule) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

/** One rule's full match+action form — `RoutingPolicyPage.tsx` renders one per `RoutingRule` in `policy.rules`, in evaluation order (first match wins, per R4). */
export function RuleEditor({ rule, index, total, fieldErrors, onChange, onRemove, onMoveUp, onMoveDown }: RuleEditorProps) {
  const prefixesId = useId();
  const weightMinId = useId();
  const weightMaxId = useId();
  const policyId = useId();
  const forceProviderId = useId();

  const prefixes = rule.match.to_postal_prefixes ?? [];

  // Every setter below OMITS the key entirely (via destructure-rest) rather
  // than assigning it `undefined` — `exactOptionalPropertyTypes` (tsconfig.base.json)
  // rejects `{ field: undefined }` against an optional field typed without
  // an explicit `| undefined` union, same convention already established in
  // api-client.ts's `request()` (see its docstring on `body`).

  function setPostalPrefixes(next: string[]) {
    const { to_postal_prefixes: _omit, ...rest } = rule.match;
    onChange({ ...rule, match: next.length > 0 ? { ...rest, to_postal_prefixes: next } : rest });
  }

  function setWeightMin(raw: string) {
    const { weight_g_min: _omit, ...rest } = rule.match;
    onChange({ ...rule, match: raw === '' ? rest : { ...rest, weight_g_min: Number(raw) } });
  }

  function setWeightMax(raw: string) {
    const { weight_g_max: _omit, ...rest } = rule.match;
    onChange({ ...rule, match: raw === '' ? rest : { ...rest, weight_g_max: Number(raw) } });
  }

  function setPolicy(raw: string) {
    const { policy: _omit, ...rest } = rule.action;
    onChange({ ...rule, action: raw === '' ? rest : { ...rest, policy: raw as RoutingPolicyName } });
  }

  function setForceProvider(raw: string) {
    const { force_provider: _omit, ...rest } = rule.action;
    onChange({ ...rule, action: raw === '' ? rest : { ...rest, force_provider: raw as ProviderId } });
  }

  function toggleExcludeProvider(provider: ProviderId, checked: boolean) {
    const current = rule.action.exclude_providers ?? [];
    const next = checked ? [...current, provider] : current.filter((p) => p !== provider);
    const { exclude_providers: _omit, ...rest } = rule.action;
    onChange({ ...rule, action: next.length > 0 ? { ...rest, exclude_providers: next } : rest });
  }

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Regra {index + 1}
        </span>
        <div className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={onMoveUp} disabled={index === 0} aria-label="Mover regra para cima">
            ↑
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMoveDown}
            disabled={index === total - 1}
            aria-label="Mover regra para baixo"
          >
            ↓
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label="Remover regra">
            Remover
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Label htmlFor={prefixesId}>Prefixos de CEP</Label>
          <PostalPrefixChips id={prefixesId} value={prefixes} onChange={setPostalPrefixes} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={weightMinId}>Peso mínimo (gramas)</Label>
          <Input
            id={weightMinId}
            type="number"
            min={0}
            value={rule.match.weight_g_min ?? ''}
            invalid={Boolean(fieldErrors?.weightMin)}
            onChange={(event) => setWeightMin(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={weightMaxId}>Peso máximo (gramas)</Label>
          <Input
            id={weightMaxId}
            type="number"
            min={0}
            value={rule.match.weight_g_max ?? ''}
            onChange={(event) => setWeightMax(event.target.value)}
          />
        </div>
      </div>
      <FieldError id={`${weightMinId}-error`} message={fieldErrors?.weightMin} />
      <FieldError id={`${prefixesId}-error`} message={fieldErrors?.match} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor={policyId}>Política</Label>
          <select
            id={policyId}
            value={rule.action.policy ?? ''}
            onChange={(event) => setPolicy(event.target.value)}
            className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <option value="">Nenhuma</option>
            {ROUTING_POLICY_NAMES.map((name) => (
              <option key={name} value={name}>
                {routingPolicyNamePt(name)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor={forceProviderId}>Forçar provedor</Label>
          <select
            id={forceProviderId}
            value={rule.action.force_provider ?? ''}
            onChange={(event) => setForceProvider(event.target.value)}
            className="rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <option value="">Nenhum</option>
            {PROVIDER_IDS.map((provider) => (
              <option key={provider} value={provider}>
                {providerIdPt(provider)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Label>Excluir provedores</Label>
        <div className="flex flex-wrap gap-4">
          {PROVIDER_IDS.map((provider) => (
            <label key={provider} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={(rule.action.exclude_providers ?? []).includes(provider)}
                onChange={(event) => toggleExcludeProvider(provider, event.target.checked)}
              />
              {providerIdPt(provider)}
            </label>
          ))}
        </div>
      </div>
      <FieldError id={`${policyId}-error`} message={fieldErrors?.action} />

      <p className="font-mono text-xs text-muted-foreground">{ruleSummary(rule, index)}</p>
    </Card>
  );
}
